import { useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/api/client';
import { useInvitationMetadata, useAcceptInvitation, useDeclineInvitation } from '@/api/hooks/usePortal';

// ── OAuth error messages ───────────────────────────────────────────────────────

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  email_mismatch: 'The email address on your Google or Microsoft account does not match the invitation email. Please use the email this invitation was sent to.',
  token_validation_failed: 'Sign-in could not be completed. Please try again.',
  acceptance_failed: 'Acceptance could not be completed. Please try again.',
  idp_denied: 'Sign-in was cancelled or denied by the provider.',
};

// ── OAuth CTA component ────────────────────────────────────────────────────────

function OAuthAcceptButton({
  provider,
  label,
  token,
  disabled,
}: {
  provider: 'google' | 'microsoft';
  label: string;
  token: string;
  disabled?: boolean;
}) {
  const initMut = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/portal/auth/oauth/${provider}/init`, {
        flow: 'acceptance',
        invitation_token: token,
      });
      return data.data as { authorization_url: string };
    },
    onSuccess: ({ authorization_url }) => {
      window.location.href = authorization_url;
    },
    onError: () => {
      toast.error(`Could not start ${label} sign-in. Please try again.`);
    },
  });

  return (
    <Button
      type="button"
      variant="outline"
      className="flex-1 gap-2"
      disabled={disabled || initMut.isPending}
      onClick={() => initMut.mutate()}
    >
      {initMut.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <span className="text-xs font-bold">{provider === 'google' ? 'G' : 'M'}</span>
      )}
      {label}
    </Button>
  );
}

// Sprint U.1 D-SU.1-9: vendor-side invitation acceptance page.
// Trap #9: token captured once via useRef; never re-read from URL after mount.
// Three branches: new_registration (password form), existing_account, soft_delete_restored (accept/decline only).

export default function PortalInvitationAcceptancePage() {
  const { token: tokenParam } = useParams<{ token: string }>();
  // Trap #9: stable reference — never changes after mount
  const tokenRef = useRef(tokenParam ?? '');
  const token = tokenRef.current;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthError = searchParams.get('error');

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const { data: metadata, isLoading, isError } = useInvitationMetadata(token);
  const acceptMutation = useAcceptInvitation(token);
  const declineMutation = useDeclineInvitation(token);

  if (!token) {
    return <InvalidInvitation />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-base">
        <Loader2 className="h-8 w-8 animate-spin text-accent-cyan" />
      </div>
    );
  }

  if (isError || !metadata) {
    return <InvalidInvitation />;
  }

  if (declined) {
    return (
      <PageShell tenantName={metadata.tenant_name} logoUrl={metadata.tenant_logo_url}>
        <p className="text-center text-text-secondary">You have declined the invitation.</p>
      </PageShell>
    );
  }

  if (accepted) {
    return (
      <PageShell tenantName={metadata.tenant_name} logoUrl={metadata.tenant_logo_url}>
        <p className="text-center text-text-secondary">Invitation accepted. Redirecting to sign in…</p>
      </PageShell>
    );
  }

  const isNewRegistration = metadata.branch_type === 'new_registration';

  async function handleAccept() {
    const dto = isNewRegistration ? { password } : {};
    acceptMutation.mutate(dto, {
      onSuccess: (result) => {
        toast.success(result.message);
        setAccepted(true);
        setTimeout(() => navigate('/portal/login'), 1500);
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.errors?.message ?? 'Something went wrong. Please try again.';
        toast.error(msg);
      },
    });
  }

  async function handleDecline() {
    declineMutation.mutate(undefined, {
      onSuccess: () => {
        setDeclined(true);
      },
      onError: () => {
        toast.error('Could not decline invitation. Please try again.');
      },
    });
  }

  const isPending = acceptMutation.isPending || declineMutation.isPending;

  return (
    <PageShell tenantName={metadata.tenant_name} logoUrl={metadata.tenant_logo_url}>
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">
          Invitation for <strong className="text-text-primary">{metadata.vendor_account_email_masked}</strong>
        </p>

        {oauthError && (
          <p className="rounded-md bg-accent-rose/10 px-3 py-2 text-sm text-accent-rose" data-testid="oauth-error">
            {OAUTH_ERROR_MESSAGES[oauthError] ?? 'Sign-in could not be completed. Please try again.'}
          </p>
        )}

        {isNewRegistration && (
          <div className="space-y-2">
            <Label htmlFor="password">Create password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 12 characters"
                disabled={isPending}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button
            onClick={handleAccept}
            disabled={isPending || (isNewRegistration && password.length < 12)}
            className="flex-1"
          >
            {acceptMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accept Invitation'}
          </Button>
          <Button
            variant="outline"
            onClick={handleDecline}
            disabled={isPending}
          >
            {declineMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Decline'}
          </Button>
        </div>

        {/* OAuth CTAs — Sprint U.2 B2C federation */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border-subtle" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-bg-surface px-2 text-text-tertiary">or continue with</span>
          </div>
        </div>

        <div className="flex gap-3">
          <OAuthAcceptButton provider="google" label="Google" token={token} disabled={isPending} />
          <OAuthAcceptButton provider="microsoft" label="Microsoft" token={token} disabled={isPending} />
        </div>
      </div>
    </PageShell>
  );
}

function PageShell({
  tenantName,
  logoUrl,
  children,
}: {
  tenantName: string;
  logoUrl: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-md rounded-xl border border-border-subtle bg-bg-surface p-8 shadow-2xl">
        {logoUrl ? (
          <img src={logoUrl} alt={tenantName} className="mb-4 h-10 object-contain" />
        ) : (
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="mb-4 h-10 w-auto" />
        )}
        <h1 className="mb-1 text-xl font-semibold text-text-primary">
          {tenantName} — Vendor Portal Invitation
        </h1>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function InvalidInvitation() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-md rounded-xl border border-border-subtle bg-bg-surface p-8 shadow-2xl text-center">
        <h1 className="text-xl font-semibold text-text-primary">Invitation Not Found</h1>
        <p className="mt-2 text-sm text-text-secondary">
          This invitation link is invalid, has expired, or has already been used.
        </p>
      </div>
    </div>
  );
}
